import { useMemo } from 'react'
import { useAppSelector } from 'hooks/reduxHooks'
import useOffers from 'hooks/Offers/useOffers'
import useOfferList from 'hooks/Offers/useOfferList'
import getOfferListKey from 'lib/offer/offerListKey'
import Places from 'constants/places'
import { FlightsClassTypes } from 'constants/flight'
import { HOTEL_OFFERS_LIMIT } from 'constants/charterFlight'
import { buildBundleOfferFilters } from '../MaldivesDirectUtils'
import { sumUpOccupancies } from 'lib/offer/occupancyUtils'
import { CalendarItemBundleData } from './types'
import { useFlightItemData } from './useFlightItemData'
import { extractPricesAndRoomsLeftFromOffers, calculateBundlePrices } from './utils'

/**
 * Lightweight hook for individual calendar item to fetch its own bundle data
 *
 * This hook is used by CalendarItem components to independently load:
 * - Maldives hotel offers (isStream) for specific dates
 * - MEL → MAL flights for specific dates
 * - Calculate bundle pricing directly from Redux state
 *
 * Key features:
 * - No useOffers dependency (reads directly from Redux)
 * - isStream for real-time offer data
 * - Independent data fetching per item
 * - Can optimize for cheapest price when optimizeForCheapest is true
 *
 * @param params - Configuration for the specific date range
 * @param params.optimizeForCheapest - If true, uses limit: 1, sortBy: 'price.asc', and minRequiredOffers: 1 for cheapest price optimization
 * @returns Bundle data including pricing and availability
 */
export function useCalendarItemData(params: {
  checkIn: string
  checkOut: string
  nights: number
  rooms: Array<App.Occupants>
  fareClass: FlightsClassTypes
  priceMode: 'total' | 'perPerson'
  optimizeForCheapest?: boolean
  charterBundleRoute?: App.CharterBundleRoute
  disabled?: boolean
}): CalendarItemBundleData {
  const { checkIn, checkOut, nights, rooms, fareClass, priceMode: _priceMode, optimizeForCheapest = false, charterBundleRoute, disabled = false } = params

  // Calculate total passengers
  const totalPassengers = useMemo(
    () => sumUpOccupancies(rooms),
    [rooms]
  )

  // Seat-eligible passengers (exclude infants): adults + children
  const seatEligiblePassengers = useMemo(
    () => sumUpOccupancies(rooms, new Set(['adults', 'children', 'teenagers'])),
    [rooms]
  )

  // Build hotel offer filters using shared utility function
  // Optimize for cheapest price if requested: limit 1, sortBy price.asc
  // Only build filters when both checkIn and checkOut dates are available
  const hotelFilters = useMemo<App.OfferListFilters | undefined>(() => {
    if (!checkIn || !checkOut) {
      return undefined
    }
    return buildBundleOfferFilters({
      destinationId: Places.Maldives.id,
      destinationName: Places.Maldives.name,
      checkIn: checkIn,
      checkOut: checkOut,
      rooms: rooms,
      limit: optimizeForCheapest ? 1 : HOTEL_OFFERS_LIMIT,
    })
  }, [checkIn, checkOut, rooms, optimizeForCheapest])

  // Use useOfferList hook - same as main search for consistency
  // Use minRequiredOffers: 1 for streaming mode when optimizing for cheapest
  const offerList = useOfferList(hotelFilters, {
    minRequiredOffers: optimizeForCheapest ? 1 : HOTEL_OFFERS_LIMIT,
    disabled: disabled,
  })
  // Use useOffers to fetch complete offer data with packages
  const [offers, offersFetching] = useOffers<App.HotelOffer>(offerList.offerIds)

  // Get metaData from Redux state to access availableRooms
  const offerMetaDataKey = useMemo(() => hotelFilters ? getOfferListKey(hotelFilters) : '', [hotelFilters])
  const offerMetaData = useAppSelector(state => state.offer.searchResultMetadata.offerMetaData[offerMetaDataKey])

  // Use useFlightItemData to get flight data (now uses useCharterFlightJourneysForHotelDates)
  const flightItemData = useFlightItemData({
    checkIn,
    checkOut,
    rooms,
    fareClass,
    charterBundleRoute,
  })

  // Calculate number of rooms multiplier for price calculation
  // package.price is per room, so we need to multiply by number of rooms
  const numberOfRooms = rooms.length

  // Calculate bundle pricing (from complete offer data with packages)
  const bundleData = useMemo(() => {
    const hotelFetching = offerList?.fetching || offersFetching
    const flightFetching = flightItemData.fetching
    // Extract prices, fees, and roomsLeft from complete offer data (includes packages with duration)
    // Extracts all fees: propertyFees, surcharge, taxesAndFees, extraGuestSurcharges
    // These fees are extracted for completeness, matching useSearchPrices logic
    // Note: For charterBundle, bundlePricing.price only includes base price + flight price
    // Fees are listed separately in defaultPricing.fees and displayed separately in UI
    const {
      lowestPrice,
      lowestMemberPrice,
      roomsLeft,
      // Fees extracted but not included in totalPrice (matching useSearchPrices bundlePricing logic)
      lowestPropertyFees: _lowestPropertyFees,
      lowestSurcharge: _lowestSurcharge,
      lowestTaxesAndFees: _lowestTaxesAndFees,
      lowestExtraGuestSurcharges: _lowestExtraGuestSurcharges,
    } = extractPricesAndRoomsLeftFromOffers(
      offers,
      nights,
      numberOfRooms,
      rooms,
      offerMetaData
    )

    // Get flight prices from useFlightItemData
    const totalFlightFare = flightItemData.totalFlightFare

    // Calculate bundle prices
    return calculateBundlePrices({
      hotelFetching,
      flightFetching,
      lowestPrice,
      lowestMemberPrice,
      totalFlightFare,
      totalPassengers,
      seatEligiblePassengers,
      seatsLeft: flightItemData.seatsLeft,
      roomsLeft,
      offerCount: offerList.offerIds.length,
    })
  }, [offerList, offers, flightItemData, nights, totalPassengers, seatEligiblePassengers, offersFetching, numberOfRooms, rooms, offerMetaData])

  return bundleData
}
